import React, { useState, useEffect, useCallback, useRef } from 'react';
import toast from 'react-hot-toast';
import { api } from '../../api';
import { Check, Smile, FileText, AlertTriangle } from 'lucide-react'; // Removed unused icons

const StepIndicator = ({ currentStep }) => {
    const steps = [
        { name: 'Enter Details', icon: <FileText size={20} />, key: 'enter_details' },
        { name: 'Scan Face', icon: <Smile size={20} />, key: 'scanning' },
        { name: 'Completed', icon: <Check size={20} />, key: 'success' },
    ];

    const getStepStatus = (stepKey) => {
        const stepIndex = steps.findIndex(s => s.key === stepKey);
        const currentIndex = steps.findIndex(s => s.key === currentStep);

        if (currentStep === 'error' && stepKey === 'scanning') return 'error';
        if (stepIndex < currentIndex) return 'completed';
        if (stepIndex === currentIndex) return 'active';
        return 'upcoming';
    };

    return (
        <nav aria-label="Progress">
            <ol role="list" className="steps-container">
                {steps.map((step, stepIdx) => (
                    <li key={step.name} className={`step-item ${stepIdx !== steps.length - 1 ? 'has-connector' : ''}`}>
                        <div className="step-content">
                            <span className={`step-circle ${getStepStatus(step.key)}`}>
                                {getStepStatus(step.key) === 'completed' ? <Check size={20} className="step-icon" /> : (getStepStatus(step.key) === 'error' ? <AlertTriangle size={20} className="step-icon" /> : step.icon)}
                            </span>
                            <span className={`step-name ${getStepStatus(step.key)}`}>{step.name}</span>
                        </div>
                        {stepIdx !== steps.length - 1 && <div className={`step-connector ${getStepStatus(steps[stepIdx+1].key)}`} />}
                    </li>
                ))}
            </ol>
        </nav>
    );
};


const AddEmployeeByFaceModal = ({ device, onClose, onComplete }) => {
  const [enrollmentStep, setEnrollmentStep] = useState('enter_details');
  const [newEmployeeId, setNewEmployeeId] = useState(null);
  const [employeeName, setEmployeeName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const pollingIntervalId = useRef(null);
  const timeoutId = useRef(null);

  // --- FIXED: Updated ID Fetching Logic ---
  useEffect(() => {
    const getNextId = async () => {
      if (!device) return;
      
      // 1. Clear ID to show loading/refresh state
      setNewEmployeeId(null);

      try {
        const response = await api.getNextEmployeeId(device.id);
        setNewEmployeeId(response.nextEmployeeId);
      } catch (error) {
        toast.error(`Failed to fetch next employee ID: ${error.message}`);
        setEnrollmentStep('error');
        setErrorMessage(`Failed to fetch next employee ID: ${error.message}`);
      }
    };

    // 2. Only fetch if we are in the 'enter_details' step
    if (enrollmentStep === 'enter_details') {
        getNextId();
    }
  }, [device.id, enrollmentStep]); // 3. Depend on enrollmentStep to allow refreshes

  const handleInitiateScan = async (e) => {
    e.preventDefault();
    if (!employeeName.trim()) {
      toast.error('Employee name is required.');
      return;
    }
    setIsSubmitting(true);
    try {
      await api.initiateFaceEnrollment(device.id, { userId: newEmployeeId });
      toast.success('Device is ready. Please ask the employee to look at the device.');
      setEnrollmentStep('scanning');
    } catch (error) {
        if (error.message && error.message.includes('MenuProcessing')) {
            toast.error('Please close any open menus on the device to begin enrollment.', { duration: 6000 });
        } else {
            toast.error(`Initiation failed: ${error.message}`);
        }
        setIsSubmitting(false);
    }
  };

  const startPolling = useCallback(() => {
    if (pollingIntervalId.current) clearInterval(pollingIntervalId.current);
    if (timeoutId.current) clearTimeout(timeoutId.current);

    timeoutId.current = setTimeout(() => {
        clearInterval(pollingIntervalId.current);
        setEnrollmentStep('error');
        setErrorMessage("Enrollment timed out. The user did not scan their face in time.");
    }, 60000);

    pollingIntervalId.current = setInterval(async () => {
      try {
        const response = await api.checkFaceEnrollmentStatus(device.id, newEmployeeId, { name: employeeName });
        
        if (response.status === 'fp_enrolled' || response.status === 'face_enrolled') {
          clearInterval(pollingIntervalId.current); 
          clearTimeout(timeoutId.current);
          toast.success(response.message || 'Employee enrolled successfully!');
          setEnrollmentStep('success');
          setTimeout(() => {
            onComplete();
            onClose();
          }, 2000);
        }
      } catch (error) {
        clearInterval(pollingIntervalId.current);
        clearTimeout(timeoutId.current);
        setEnrollmentStep('error');
        setErrorMessage(error.message || 'An error occurred while checking enrollment status.');
      }
    }, 3000);
  }, [device.id, newEmployeeId, employeeName, onClose, onComplete]);

  useEffect(() => {
    if (enrollmentStep === 'scanning') {
      startPolling();
    }
    return () => {
      if (pollingIntervalId.current) clearInterval(pollingIntervalId.current);
      if (timeoutId.current) clearTimeout(timeoutId.current);
    };
  }, [enrollmentStep, startPolling]);
  
  const handleRetry = () => {
    setEnrollmentStep('enter_details');
    setErrorMessage('');
    setIsSubmitting(false);
  };
  
  const renderContent = () => {
    switch (enrollmentStep) {
      case 'enter_details':
        return (
          <form onSubmit={handleInitiateScan} className="modal-step-content">
            <p className="form-help-text">Enter the employee's name. This name will be saved automatically upon a successful scan.</p>
            <div className="form-group">
                <label className="form-label" htmlFor="employeeName">Employee Name *</label>
                <input
                    id="employeeName" type="text" value={employeeName}
                    onChange={(e) => setEmployeeName(e.target.value)}
                    className="form-input" required placeholder="e.g., Jane Doe"
                    disabled={isSubmitting || !newEmployeeId}
                />
            </div>
            <div className="form-group">
              <label className="form-label">New Employee ID</label>
              <input type="text" value={newEmployeeId !== null ? newEmployeeId : 'Loading...'} disabled className="form-input" />
            </div>
            <div className="modal-actions">
              <button type="button" onClick={onClose} className="button button-secondary" disabled={isSubmitting}>Cancel</button>
              <button type="submit" className="button action-button" disabled={isSubmitting || !newEmployeeId}>
                {isSubmitting ? 'Starting...' : 'Continue to Scan'}
              </button>
            </div>
          </form>
        );

      case 'scanning':
        return (
          <div className="modal-step-content text-center">
            <div className="spinner"></div>
            <p className="mt-4 font-semibold">Waiting for Face Scan</p>
            <p className="form-help-text">Please ask the user to look at the device camera.</p>
          </div>
        );

      case 'success':
        return (
            <div className="modal-step-content text-center">
                <p className="font-semibold text-green-600">✅ Enrollment Complete!</p>
                <p className="form-help-text">The modal will close automatically.</p>
            </div>
        );

      case 'error':
        return (
            <div className="modal-step-content text-center">
                <p className="font-semibold text-red-600">Enrollment Failed</p>
                <p className="form-help-text mb-4">{errorMessage}</p>
                <div className="modal-actions">
                    <button type="button" onClick={onClose} className="button button-secondary">Close</button>
                    <button type="button" onClick={handleRetry} className="button action-button">Retry</button>
                </div>
            </div>
        );

      default:
        return <p>An unexpected error occurred.</p>;
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
            <h2 className="modal-title">Create New Employee via Face Scan</h2>
        </div>
        <div className="modal-body">
            <StepIndicator currentStep={enrollmentStep} />
            {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default AddEmployeeByFaceModal;